import { FC, useEffect, useState } from 'react';

import { MaxNodesInputProps } from './types';

/**
 * MaxNodesInput - Input field for controlling maximum node representation
 *
 * Provides a number input with validation for limiting the maximum number of nodes
 * displayed in the graph visualization. Includes automatic synchronization with
 * parent state and validation on blur/enter keypress.
 *
 * Features:
 * - Local state for immediate UI feedback
 * - Enter key submission
 * - Blur event submission
 * - Validation (positive integers only)
 * - Auto-revert on invalid input
 *
 * @param maxNodes - Current maximum nodes value
 * @param onMaxNodesChange - Callback when value changes
 */
export const MaxNodesInput: FC<MaxNodesInputProps> = ({
  maxNodes,
  onMaxNodesChange,
}) => {
  const [localMaxNodes, setLocalMaxNodes] = useState(maxNodes.toString());

  // Sync localMaxNodes with maxNodes prop changes
  useEffect(() => {
    setLocalMaxNodes(maxNodes.toString());
  }, [maxNodes]);

  const handleSubmit = () => {
    const value = parseInt(localMaxNodes, 10);
    if (!isNaN(value) && value > 0) {
      onMaxNodesChange?.(value);
    } else {
      // Revert to previous valid value
      setLocalMaxNodes(maxNodes.toString());
    }
  };

  return (
    <div className="border-chariot-stroke mt-4 flex items-center justify-end gap-2 border-t pt-3">
      <label className="text-chariot-text-primary text-sm font-medium whitespace-nowrap">
        Max Node Representation
      </label>
      <input
        type="number"
        value={localMaxNodes}
        onChange={e => setLocalMaxNodes(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleSubmit();
          }
        }}
        onBlur={handleSubmit}
        className="bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary focus:border-chariot-brand-primary w-20 rounded border px-2 py-1 text-right text-sm focus:outline-none"
        min="1"
        max="99999"
      />
    </div>
  );
};
